"use client";

import type { CSSProperties, Dispatch, SetStateAction } from "react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterX, MapPin, Pencil, Plus, Trash2 } from "lucide-react";

import { Button, ListCount, ListSurface, ListSurfaceBody, ListSurfaceFooter, ListSurfaceHeader, ListToolbar, ListToolbarGroup, ToolbarIconButton } from "@/components/ds";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { tableHeaderSearchInputClass, ThemedSelect } from "@/components/ui-system/select";
import { isAppHttpError } from "@/lib/api/client";
import {
  createOrganization,
  deleteOrganization,
  listIndianStates,
  listOrganizationTags,
  listOrganizations,
  updateOrganization,
  type IndianStateDto,
  type OrganizationTagDto,
} from "@/lib/api/endpoints/organizations";
import {
  createOrganizationLocation,
  deleteOrganizationLocation,
  listOrganizationLocations,
  updateOrganizationLocation,
} from "@/lib/api/endpoints/organization-locations";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

type OrgItem = {
  id: string;
  name: string;
  contact_email: string | null;
  phone: string | null;
  website: string | null;
  assignee_name: string | null;
  assignee_email: string | null;
  is_active: boolean;
  tags: OrganizationTagDto[];
};

type OrgLocationItem = {
  id: string;
  organization_id: string;
  tenant_id: string | null;
  name: string;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state_id: string | null;
  state_name: string | null;
  state_code: string | null;
  pincode: string | null;
  latitude: number | null;
  longitude: number | null;
  is_primary: boolean;
  note: string | null;
};

type OrgFormState = {
  name: string;
  contact_email: string;
  phone: string;
  website: string;
  location_address_line_1: string;
  location_address_line_2: string;
  location_city: string;
  location_state_id: string;
  location_pincode: string;
  location_is_primary: boolean;
  location_note: string;
  tag_ids: string[];
  new_tags: string;
  assignee_name: string;
  assignee_email: string;
  is_active: boolean;
};

type LocationFormState = {
  name: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state_id: string;
  pincode: string;
  latitude: string;
  longitude: string;
  is_primary: boolean;
  note: string;
};

type OrgFieldKey = "name";
type OrgCreateFieldKey = OrgFieldKey | "location_address_line_1" | "location_city" | "location_state_id" | "location_pincode";
type LocationFieldKey = "name" | "address_line_1" | "city" | "state_id" | "pincode";

const EMPTY_FORM: OrgFormState = {
  name: "",
  contact_email: "",
  phone: "",
  website: "",
  location_address_line_1: "",
  location_address_line_2: "",
  location_city: "",
  location_state_id: "",
  location_pincode: "",
  location_is_primary: true,
  location_note: "",
  tag_ids: [],
  new_tags: "",
  assignee_name: "",
  assignee_email: "",
  is_active: true,
};

const EMPTY_LOCATION_FORM: LocationFormState = {
  name: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state_id: "",
  pincode: "",
  latitude: "",
  longitude: "",
  is_primary: false,
  note: "",
};

const SURFACE_BORDER = "var(--card-border)";
const SURFACE_SHADOW = "var(--shadow-raised)";
const SURFACE_HEADER = "var(--bg-surface)";

const LocationPickMap = lazy(() =>
  import("@/modules/core/datalogger-create/components/location-pick-map").then((m) => ({ default: m.LocationPickMap })),
);

const locationMapFallback = (
  <div className="flex h-[14rem] items-center justify-center rounded-card border border-card-border bg-surface-secondary text-caption text-muted">
    Loading map...
  </div>
);
const ORG_PAGE_SIZE_OPTIONS = [10, 25, 50];

const S = {
  page: {
    display: "grid",
    gap: "20px",
    paddingTop: "0px",
    padding: "20px",
    color: "#1f2937",
    fontFamily: "'Poppins', 'Segoe UI', system-ui, sans-serif",
  } as CSSProperties,
  panel: {
    background: "var(--bg-surface)",
    border: `1px solid ${SURFACE_BORDER}`,
    borderRadius: "20px",
    boxShadow: SURFACE_SHADOW,
    overflow: "hidden",
  } as CSSProperties,
  panelHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "16px 18px",
    borderBottom: `1px solid ${SURFACE_BORDER}`,
    background: SURFACE_HEADER,
  } as CSSProperties,
  panelTitle: {
    margin: 0,
    fontSize: "15px",
    fontWeight: 700,
    color: "var(--foreground)",
  } as CSSProperties,
  panelSub: {
    margin: "4px 0 0",
    fontSize: "12px",
    color: "var(--muted)",
  } as CSSProperties,
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  } as CSSProperties,
  search: {
    height: "40px",
    minWidth: "240px",
    flex: 1,
    padding: "0 12px",
    borderRadius: "14px",
    border: `1px solid ${SURFACE_BORDER}`,
    background: "var(--bg-surface)",
    outline: "none",
    fontSize: "13px",
  } as CSSProperties,
  primaryBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minHeight: "38px",
    padding: "0 14px",
    border: "1px solid var(--btn-primary-to)",
    borderRadius: "12px",
    background: "var(--btn-primary-to)",
    color: "var(--btn-primary-fg)",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 18px 38px -24px rgba(199,58,58,0.38)",
  } as CSSProperties,
  secondaryBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minHeight: "36px",
    padding: "0 12px",
    border: `1px solid ${SURFACE_BORDER}`,
    borderRadius: "12px",
    background: "var(--bg-surface)",
    color: "#2f4f41",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
  } as CSSProperties,
  dangerBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minHeight: "36px",
    padding: "0 12px",
    border: "1px solid color-mix(in srgb, var(--error) 35%, transparent)",
    borderRadius: "12px",
    background: "color-mix(in srgb, var(--error) 10%, var(--card))",
    color: "var(--error)",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
  } as CSSProperties,
  actionIconBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "36px",
    minWidth: "36px",
    padding: 0,
    border: `1px solid ${SURFACE_BORDER}`,
    borderRadius: "12px",
    background: "var(--bg-surface)",
    color: "#2563eb",
    cursor: "pointer",
  } as CSSProperties,
  dangerIconBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "36px",
    minWidth: "36px",
    padding: 0,
    border: "1px solid color-mix(in srgb, var(--error) 35%, transparent)",
    borderRadius: "12px",
    background: "color-mix(in srgb, var(--error) 10%, var(--card))",
    color: "var(--error)",
    cursor: "pointer",
  } as CSSProperties,
  tableWrap: {
    overflowX: "auto",
    borderRadius: "18px",
    borderTop: "1px solid var(--card-border)",
    background: "var(--bg-surface)",
  } as CSSProperties,
  table: {
    width: "100%",
    borderCollapse: "collapse",
  } as CSSProperties,
  thead: {
    background: SURFACE_HEADER,
  } as CSSProperties,
  th: {
    padding: "12px 14px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "var(--muted)",
    borderBottom: "1px solid var(--table-border)",
    whiteSpace: "nowrap",
  } as CSSProperties,
  td: {
    padding: "13px 14px",
    borderBottom: "1px solid var(--table-border)",
    verticalAlign: "middle",
    fontSize: "13px",
  } as CSSProperties,
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 10px",
    borderRadius: "999px",
    border: "1px solid transparent",
    fontSize: "11px",
    fontWeight: 700,
    whiteSpace: "nowrap",
  } as CSSProperties,
  emptyCard: {
    border: `1px solid ${SURFACE_BORDER}`,
    borderRadius: "20px",
    background: "var(--bg-surface)",
    padding: "32px 20px",
    textAlign: "center",
    boxShadow: SURFACE_SHADOW,
  } as CSSProperties,
  checkboxCard: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 14px",
    border: `1px solid ${SURFACE_BORDER}`,
    borderRadius: "12px",
    background: "var(--bg-surface)",
  } as CSSProperties,
} satisfies Record<string, CSSProperties>;

function errorDetail(error: unknown): string {
  if (isAppHttpError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Request failed";
}

function normalizeOrg(raw: unknown): OrgItem {
  const item = raw as Partial<OrgItem> & { id?: string | number };
  return {
    id: String(item.id ?? ""),
    name: item.name ?? "Unnamed organization",
    contact_email: item.contact_email ?? null,
    phone: item.phone ?? null,
    website: item.website ?? null,
    assignee_name: item.assignee_name ?? null,
    assignee_email: item.assignee_email ?? null,
    is_active: item.is_active ?? true,
    tags: item.tags ?? [],
  };
}

function normalizeLocation(raw: unknown): OrgLocationItem {
  const item = raw as Partial<OrgLocationItem> & { id?: string | number };
  return {
    id: String(item.id ?? ""),
    organization_id: item.organization_id ?? "",
    tenant_id: item.tenant_id ?? "",
    name: item.name ?? "Unnamed location",
    address_line_1: item.address_line_1 ?? null,
    address_line_2: item.address_line_2 ?? null,
    city: item.city ?? null,
    state_id: item.state_id ?? null,
    state_name: item.state_name ?? null,
    state_code: item.state_code ?? null,
    pincode: item.pincode ?? null,
    latitude: item.latitude ?? null,
    longitude: item.longitude ?? null,
    is_primary: item.is_primary ?? false,
    note: item.note ?? null,
  };
}

function LocationModal({
  title,
  form,
  setForm,
  stateOptions,
  submitting,
  onClose,
  onSubmit,
}: {
  title: string;
  form: LocationFormState;
  setForm: Dispatch<SetStateAction<LocationFormState>>;
  stateOptions: IndianStateDto[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const [errors, setErrors] = useState<Partial<Record<LocationFieldKey, string>>>({});
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setErrors({});
  }, [title]);

  function validate() {
    const next: Partial<Record<LocationFieldKey, string>> = {};
    if (!form.name.trim()) {
      next.name = "Location name is required.";
    }
    if (!form.address_line_1.trim()) {
      next.address_line_1 = "Address line 1 is required.";
    }
    if (!form.city.trim()) {
      next.city = "City is required.";
    }
    if (!form.state_id) {
      next.state_id = "State is required.";
    }
    if (!/^\d{6}$/.test(form.pincode.trim())) {
      next.pincode = "Enter a six digit pincode.";
    }
    return next;
  }

  function handleSubmit() {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length) {
      nameRef.current?.focus();
      return;
    }
    onSubmit();
  }

  const handleMapPick = useCallback(
    (latitude: number, longitude: number) => {
      setForm((prev) => ({
        ...prev,
        latitude: String(latitude),
        longitude: String(longitude),
      }));
    },
    [setForm],
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1250,
        background: "rgba(15,23,42,0.22)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "860px",
          maxHeight: "calc(100dvh - 48px)",
          borderRadius: "24px",
          border: `1px solid ${SURFACE_BORDER}`,
          background: "var(--card)",
          boxShadow: "0 24px 60px -32px rgba(15,23,42,0.24)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            padding: "18px 22px",
            borderBottom: `1px solid ${SURFACE_BORDER}`,
            background: SURFACE_HEADER,
          }}
        >
          <div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#1f2937" }}>
              {title}
            </div>
            <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "3px" }}>
              Manage an organization location within the active workspace.
            </div>
          </div>
          <button onClick={onClose} style={S.secondaryBtn} type="button">
            Close
          </button>
        </div>

        <div
          style={{
            padding: "22px",
            display: "grid",
            gap: "18px",
            overflowY: "auto",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
            <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
              <span className="form-label form-label-required">Name</span>
              <input
                ref={nameRef}
                className={`ui-control${errors.name ? " form-field-invalid" : ""}`}
                value={form.name}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, name: e.target.value }));
                  setErrors((prev) => ({ ...prev, name: undefined }));
                }}
              />
              {errors.name ? <span className="form-error">{errors.name}</span> : <span className="form-helper">Use a clear label that will also make sense in site and map views.</span>}
            </label>
            <label style={{ ...S.checkboxCard, fontSize: "12px", fontWeight: 600, color: "#374151" }}>
              <input
                type="checkbox"
                checked={form.is_primary}
                onChange={(e) => setForm((prev) => ({ ...prev, is_primary: e.target.checked }))}
                style={{ width: "18px", height: "18px" }}
              />
              Primary location
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
            <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
              <span className="form-label form-label-required">Address line 1</span>
              <input className={`ui-control${errors.address_line_1 ? " form-field-invalid" : ""}`} value={form.address_line_1} onChange={(e) => setForm((prev) => ({ ...prev, address_line_1: e.target.value }))} />
              {errors.address_line_1 ? <span className="form-error">{errors.address_line_1}</span> : null}
            </label>
            <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
              <span className="form-label">Address line 2</span>
              <input className="ui-control" value={form.address_line_2} onChange={(e) => setForm((prev) => ({ ...prev, address_line_2: e.target.value }))} />
            </label>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "16px",
            }}
          >
            <label
              style={{
                display: "grid",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#374151",
                width: "100%",
              }}
            >
              <span className="form-label form-label-required">City</span>
              <input
                className={`ui-control${errors.city ? " form-field-invalid" : ""}`}
                value={form.city}
                onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
              />
              {errors.city ? <span className="form-error">{errors.city}</span> : null}
            </label>
            <label
              style={{
                display: "grid",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#374151",
                width: "100%",
              }}
            >
              <span className="form-label form-label-required">State</span>
              <ThemedSelect
                value={form.state_id}
                onChange={(value) => setForm((prev) => ({ ...prev, state_id: value }))}
                options={[
                  { value: "", label: "Select state" },
                  ...stateOptions.map((state) => ({ value: state.id, label: state.name, description: state.code })),
                ]}
                ariaLabel="State"
              />
              {errors.state_id ? <span className="form-error">{errors.state_id}</span> : null}
            </label>
            <label
              style={{
                display: "grid",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#374151",
                width: "100%",
              }}
            >
              <span className="form-label form-label-required">Pincode</span>
              <input
                className={`ui-control${errors.pincode ? " form-field-invalid" : ""}`}
                value={form.pincode}
                inputMode="numeric"
                maxLength={6}
                onChange={(e) => setForm((prev) => ({ ...prev, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
              />
              {errors.pincode ? <span className="form-error">{errors.pincode}</span> : null}
            </label>
          </div>

          <div style={{ height: "240px", minHeight: "240px", width: "100%" }}>
            <Suspense fallback={locationMapFallback}>
              <LocationPickMap
                latitude={form.latitude}
                longitude={form.longitude}
                onPick={handleMapPick}
                className="relative h-full overflow-hidden rounded-card border border-card-border bg-surface-secondary"
              />
            </Suspense>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
            <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
              <span className="form-label">Latitude</span>
              <input className="ui-control" value={form.latitude} onChange={(e) => setForm((prev) => ({ ...prev, latitude: e.target.value }))} />
            </label>
            <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
              <span className="form-label">Longitude</span>
              <input className="ui-control" value={form.longitude} onChange={(e) => setForm((prev) => ({ ...prev, longitude: e.target.value }))} />
            </label>
          </div>

          <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
            <span className="form-label">Note</span>
            <textarea
              className="ui-control"
              value={form.note}
              onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
              rows={3}
            />
            <span className="form-helper">Optional handoff notes for installers, dispatchers, or support.</span>
          </label>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", padding: "18px 22px 22px", borderTop: `1px solid ${SURFACE_BORDER}` }}>
          <button type="button" onClick={onClose} style={S.secondaryBtn}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} style={S.primaryBtn} disabled={submitting}>
            {submitting ? "Saving..." : "Save location"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LocationsPanel({
  org,
  locations,
  canCreate,
  canEdit,
  canDelete,
  loading,
  error,
  onBack,
  onAdd,
  onEdit,
  onDelete,
}: {
  org: OrgItem;
  locations: OrgLocationItem[];
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onAdd: () => void;
  onEdit: (location: OrgLocationItem) => void;
  onDelete: (location: OrgLocationItem) => void;
}) {
  return (
    <ListSurface>
      <ListSurfaceHeader>
        <div style={{ display: "grid", gap: "8px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={onBack}
              style={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: "30px",
                padding: "0 12px",
                borderRadius: "999px",
                border: "1px solid rgba(37,99,235,0.18)",
                background: "rgba(37,99,235,0.08)",
                color: "#1d4ed8",
                fontSize: "12px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {org.name}
            </button>
            <span style={{ color: "var(--muted)", fontSize: "14px", fontWeight: 700 }}>-&gt;</span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: "30px",
                padding: "0 12px",
                borderRadius: "999px",
                border: "1px solid rgba(16,185,129,0.22)",
                background: "rgba(16,185,129,0.1)",
                color: "#047857",
                fontSize: "12px",
                fontWeight: 700,
              }}
            >
              Locations
            </span>
          </div>
          <p className="text-xs text-muted">
            Manage physical sites, service addresses, and primary routing location for the selected organization.
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              ...S.secondaryBtn,
              borderColor: "rgba(37,99,235,0.2)",
              background: "rgba(37,99,235,0.1)",
              color: "#1d4ed8",
            }}
          >
            Organizations
          </button>
          {canCreate ? (
            <button type="button" style={S.primaryBtn} onClick={onAdd}>
              + Add location
            </button>
          ) : null}
        </div>
      </ListSurfaceHeader>

      <ListSurfaceBody style={{ padding: locations.length ? 0 : "22px" }}>
        {loading ? (
          <div style={{ color: "var(--muted)", padding: "18px" }}>Loading locations...</div>
        ) : error ? (
          <div style={{ color: "#dc2626", padding: "18px" }}>{error}</div>
        ) : !locations.length ? (
          <div style={S.emptyCard}>
            <div style={{ fontSize: "16px", fontWeight: 700, color: "#1f2937" }}>No locations yet</div>
            <div style={{ marginTop: "8px", fontSize: "13px", color: "var(--muted)" }}>
              Add a primary or secondary location for this organization.
            </div>
            {canCreate ? (
              <div style={{ marginTop: "16px" }}>
                <button type="button" style={S.primaryBtn} onClick={onAdd}>
                  Add location
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead style={S.thead}>
                <tr>
                  <th style={S.th}>Location</th>
                  <th style={S.th}>Address</th>
                  <th style={S.th}>City</th>
                  <th style={S.th}>State</th>
                  <th style={S.th}>Pincode</th>
                  <th style={S.th}>Primary</th>
                  {canEdit || canDelete ? <th style={{ ...S.th, textAlign: "right" }}>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {locations.map((location, index) => (
                  <tr key={location.id} style={{ background: index % 2 === 1 ? "rgba(59,130,246,0.05)" : "var(--bg-surface)" }}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600, color: "var(--foreground)" }}>{location.name}</div>
                      {location.note ? (
                        <div style={{ color: "var(--muted)", fontSize: "12px", marginTop: "3px" }}>
                          {location.note}
                        </div>
                      ) : null}
                    </td>
                    <td style={S.td}>
                      <div style={{ display: "grid", gap: "2px" }}>
                        <span>{location.address_line_1 ?? "-"}</span>
                        <span style={{ color: "var(--muted)", fontSize: "12px" }}>{location.address_line_2 ?? ""}</span>
                      </div>
                    </td>
                    <td style={S.td}>{location.city ?? "-"}</td>
                    <td style={S.td}>{location.state_name ?? "-"}</td>
                    <td style={S.td}>{location.pincode ?? "-"}</td>
                    <td style={S.td}>
                      <span
                        style={{
                          ...S.badge,
                          borderColor: location.is_primary ? "#bbf7d0" : "var(--card-border)",
                          background: location.is_primary ? "#dcfce7" : "var(--bg-surface)",
                          color: location.is_primary ? "#166534" : "#64748b",
                        }}
                      >
                        {location.is_primary ? "Primary" : "Secondary"}
                      </span>
                    </td>
                    {canEdit || canDelete ? (
                      <td style={S.td}>
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                          {canEdit ? (
                            <button
                              type="button"
                              style={S.actionIconBtn}
                              onClick={() => onEdit(location)}
                              title={`Edit ${location.name}`}
                              aria-label={`Edit ${location.name}`}
                            >
                              <Pencil size={14} aria-hidden />
                            </button>
                          ) : null}
                          {canDelete ? (
                            <button
                              type="button"
                              style={S.dangerIconBtn}
                              onClick={() => onDelete(location)}
                              title={`Delete ${location.name}`}
                              aria-label={`Delete ${location.name}`}
                            >
                              <Trash2 size={14} aria-hidden />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ListSurfaceBody>
    </ListSurface>
  );
}

function OrganizationModal({
  title,
  form,
  setForm,
  stateOptions,
  tagOptions,
  includeFirstLocation = false,
  submitting,
  onClose,
  onSubmit,
}: {
  title: string;
  form: OrgFormState;
  setForm: Dispatch<SetStateAction<OrgFormState>>;
  stateOptions: IndianStateDto[];
  tagOptions: OrganizationTagDto[];
  includeFirstLocation?: boolean;
  submitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const [errors, setErrors] = useState<Partial<Record<OrgCreateFieldKey, string>>>({});
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setErrors({});
  }, [title]);

  function validate() {
    const next: Partial<Record<OrgCreateFieldKey, string>> = {};
    if (!form.name.trim()) {
      next.name = "Organization name is required.";
    }
    if (includeFirstLocation) {
      if (!form.location_address_line_1.trim()) {
        next.location_address_line_1 = "Address line 1 is required.";
      }
      if (!form.location_city.trim()) {
        next.location_city = "City is required.";
      }
      if (!form.location_state_id) {
        next.location_state_id = "State is required.";
      }
      if (!/^\d{6}$/.test(form.location_pincode.trim())) {
        next.location_pincode = "Enter a six digit pincode.";
      }
    }
    return next;
  }

  function focusFirstInvalid(next: Partial<Record<OrgCreateFieldKey, string>>) {
    if (next.name) {
      nameRef.current?.focus();
    }
  }

  function handleSubmit() {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length) {
      focusFirstInvalid(next);
      return;
    }
    onSubmit();
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgba(15,23,42,0.22)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "760px",
          maxHeight: "calc(100dvh - 48px)",
          borderRadius: "24px",
          border: `1px solid ${SURFACE_BORDER}`,
          background: "var(--card)",
          boxShadow: "0 24px 60px -32px rgba(15,23,42,0.24)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            padding: "18px 22px",
            borderBottom: `1px solid ${SURFACE_BORDER}`,
            background: SURFACE_HEADER,
          }}
        >
          <div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#1f2937" }}>
              {title}
            </div>
            <div
              style={{ fontSize: "12px", color: "var(--muted)", marginTop: "3px" }}
            >
              Manage organization identity and assignee metadata.
            </div>
          </div>
          <button onClick={onClose} style={S.secondaryBtn} type="button">
            Close
          </button>
        </div>

        <div
          style={{
            padding: "22px",
            display: "grid",
            gap: "18px",
            overflowY: "auto",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "14px",
            }}
          >
            <label
              style={{
                display: "grid",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#374151",
              }}
            >
              <span className="form-label form-label-required">Name</span>
              <input
                ref={nameRef}
                className={`ui-control${errors.name ? " form-field-invalid" : ""}`}
                value={form.name}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, name: e.target.value }));
                  setErrors((prev) => ({ ...prev, name: undefined }));
                }}
              />
              {errors.name ? (
                <span className="form-error">{errors.name}</span>
              ) : (
                // <span className="form-helper">Use the full business or workspace-facing name.</span>
                <></>
              )}
            </label>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "14px",
            }}
          >
            <label
              style={{
                display: "grid",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#374151",
              }}
            >
              <span className="form-label">Contact email</span>
              <input
                className="ui-control"
                value={form.contact_email}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    contact_email: e.target.value,
                  }))
                }
              />
            </label>
            <label
              style={{
                display: "grid",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#374151",
              }}
            >
              <span className="form-label">Phone</span>
              <input
                className="ui-control"
                value={form.phone}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
              />
            </label>
          </div>

          <label
            style={{
              display: "grid",
              gap: "6px",
              fontSize: "12px",
              fontWeight: 600,
              color: "#374151",
            }}
          >
            <span className="form-label">Website</span>
            <input
              className="ui-control"
              value={form.website}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  website: e.target.value,
                }))
              }
            />
          </label>

          <div style={{ display: "grid", gap: "10px" }}>
            <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--foreground)" }}>Tags</div>
            {tagOptions.length ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {tagOptions.map((tag) => {
                  const checked = form.tag_ids.includes(tag.id);
                  return (
                    <label
                      key={tag.id}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        minHeight: "34px",
                        padding: "0 10px",
                        borderRadius: "999px",
                        border: checked ? "1px solid #93c5fd" : `1px solid ${SURFACE_BORDER}`,
                        background: checked ? "#eff6ff" : "var(--bg-surface)",
                        color: checked ? "#1d4ed8" : "#475569",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            tag_ids: e.target.checked
                              ? [...prev.tag_ids, tag.id]
                              : prev.tag_ids.filter((id) => id !== tag.id),
                          }))
                        }
                      />
                      {tag.name}
                    </label>
                  );
                })}
              </div>
            ) : null}
            <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
              <span className="form-label">Create tags</span>
              <input
                className="ui-control"
                value={form.new_tags}
                onChange={(e) => setForm((prev) => ({ ...prev, new_tags: e.target.value }))}
                placeholder="Installation partner, Key account"
              />
              <span className="form-helper">Separate multiple new tags with commas.</span>
            </label>
          </div>

          {includeFirstLocation ? (
            <div style={{ display: "grid", gap: "14px", paddingTop: "4px" }}>
              <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--foreground)" }}>Primary location</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
                  <span className="form-label form-label-required">Address line 1</span>
                  <input
                    className={`ui-control${errors.location_address_line_1 ? " form-field-invalid" : ""}`}
                    value={form.location_address_line_1}
                    onChange={(e) => setForm((prev) => ({ ...prev, location_address_line_1: e.target.value }))}
                  />
                  {errors.location_address_line_1 ? <span className="form-error">{errors.location_address_line_1}</span> : null}
                </label>
                <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
                  <span className="form-label">Address line 2</span>
                  <input
                    className="ui-control"
                    value={form.location_address_line_2}
                    onChange={(e) => setForm((prev) => ({ ...prev, location_address_line_2: e.target.value }))}
                  />
                </label>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
                  <span className="form-label form-label-required">City</span>
                  <input
                    className={`ui-control${errors.location_city ? " form-field-invalid" : ""}`}
                    value={form.location_city}
                    onChange={(e) => setForm((prev) => ({ ...prev, location_city: e.target.value }))}
                  />
                  {errors.location_city ? <span className="form-error">{errors.location_city}</span> : null}
                </label>
                <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
                  <span className="form-label form-label-required">State</span>
                  <ThemedSelect
                    value={form.location_state_id}
                    onChange={(value) => setForm((prev) => ({ ...prev, location_state_id: value }))}
                    options={[
                      { value: "", label: "Select state" },
                      ...stateOptions.map((state) => ({ value: state.id, label: state.name, description: state.code })),
                    ]}
                    ariaLabel="Primary location state"
                  />
                  {errors.location_state_id ? <span className="form-error">{errors.location_state_id}</span> : null}
                </label>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
                  <span className="form-label form-label-required">Pincode</span>
                  <input
                    className={`ui-control${errors.location_pincode ? " form-field-invalid" : ""}`}
                    value={form.location_pincode}
                    inputMode="numeric"
                    maxLength={6}
                    onChange={(e) => setForm((prev) => ({ ...prev, location_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                  />
                  {errors.location_pincode ? <span className="form-error">{errors.location_pincode}</span> : null}
                </label>
                <label style={{ ...S.checkboxCard, fontSize: "12px", fontWeight: 600, color: "#374151" }}>
                  <input
                    type="checkbox"
                    checked={form.location_is_primary}
                    onChange={(e) => setForm((prev) => ({ ...prev, location_is_primary: e.target.checked }))}
                    style={{ width: "18px", height: "18px" }}
                  />
                  Make primary
                </label>
              </div>
              <label style={{ display: "grid", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#374151" }}>
                <span className="form-label">Note</span>
                <textarea
                  className="ui-control"
                  value={form.location_note}
                  onChange={(e) => setForm((prev) => ({ ...prev, location_note: e.target.value }))}
                  rows={3}
                />
              </label>
            </div>
          ) : null}

          {/* <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "14px",
            }}
          >
            <label
              style={{
                display: "grid",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#374151",
              }}
            >
              <span className="form-label">Assignee name</span>
              <input
                className="ui-control"
                value={form.assignee_name}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    assignee_name: e.target.value,
                  }))
                }
              />
            </label>
            <label
              style={{
                display: "grid",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#374151",
              }}
            >
              <span className="form-label">Assignee email</span>
              <input
                className="ui-control"
                value={form.assignee_email}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    assignee_email: e.target.value,
                  }))
                }
              />
            </label>
          </div> */}

          <label style={{ ...S.checkboxCard, fontSize: "13px", color: "#374151", fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, is_active: e.target.checked }))
              }
            />
            Active organization
          </label>
          <span className="form-helper">Inactive organizations stay in the hierarchy but can be treated as archived.</span>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            padding: "18px 22px 22px",
            borderTop: `1px solid ${SURFACE_BORDER}`,
          }}
        >
          <button type="button" onClick={onClose} style={S.secondaryBtn}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            style={S.primaryBtn}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Save organization"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function OrganizationsPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.role === "super_admin";
  const { permissionMap } = useEffectivePermissions();
  const canCreateOrganizations = isSuperAdmin;
  const canEditOrganizations = isSuperAdmin;
  const canDeleteOrganizations = isSuperAdmin;
  const canCreateLocations = hasModulePermission(user, permissionMap, "locations", "create");
  const canEditLocations = hasModulePermission(user, permissionMap, "locations", "edit");
  const canDeleteLocations = hasModulePermission(user, permissionMap, "locations", "delete");
  const isOwnOrganization = (org: OrgItem) => isSuperAdmin || user?.organizationId === org.id;
  const canEditOrg = (_org: OrgItem) => canEditOrganizations;
  const canCreateLocationForOrg = (org: OrgItem) => canCreateLocations && isOwnOrganization(org);
  const canEditLocationForOrg = (org: OrgItem) => canEditLocations && isOwnOrganization(org);
  const canDeleteLocationForOrg = (org: OrgItem) => canDeleteLocations && isOwnOrganization(org);

  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editingOrg, setEditingOrg] = useState<OrgItem | null>(null);
  const [form, setForm] = useState<OrgFormState>(EMPTY_FORM);
  const [toast, setToast] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<OrgLocationItem | null>(null);
  const [locationForm, setLocationForm] = useState<LocationFormState>(EMPTY_LOCATION_FORM);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(25);

  const statesQuery = useQuery({
    queryKey: ["reference", "indian-states"],
    queryFn: listIndianStates,
    staleTime: 24 * 60 * 60 * 1000,
  });
  const stateOptions = statesQuery.data ?? [];

  const tagsQuery = useQuery({
    queryKey: ["organizations", "tags"],
    queryFn: listOrganizationTags,
    enabled: isSuperAdmin,
    staleTime: 60_000,
  });
  const tagOptions = tagsQuery.data ?? [];

  const q = useQuery({
    queryKey: ["organizations", "global", "tree"],
    queryFn: () => listOrganizations(),
    enabled: Boolean(user),
  });

  const orgs = useMemo<OrgItem[]>(() => {
    const rawItems = q.data?.items ?? [];
    return rawItems.map(normalizeOrg);
  }, [q.data?.items]);

  const selectedOrg = useMemo(
    () => orgs.find((item) => item.id === selectedOrgId) ?? null,
    [orgs, selectedOrgId],
  );

  useEffect(() => {
    if (selectedOrgId && !orgs.some((item) => item.id === selectedOrgId)) {
      setSelectedOrgId(null);
    }
  }, [orgs, selectedOrgId]);

  const locationsQuery = useQuery({
    queryKey: ["organization-locations", selectedOrgId ?? "none"],
    queryFn: async () => {
      if (!selectedOrgId) {
        return { items: [], total: 0 };
      }
      return listOrganizationLocations(selectedOrgId);
    },
    enabled: Boolean(selectedOrgId),
  });

  const locations = useMemo<OrgLocationItem[]>(
    () => (locationsQuery.data?.items ?? []).map(normalizeLocation),
    [locationsQuery.data?.items],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return orgs.filter((o) => {
      const haystack = [
        o.name,
        o.phone ?? "",
        o.website ?? "",
        o.assignee_name ?? "",
        o.assignee_email ?? "",
        ...o.tags.map((tag) => tag.name),
        o.id,
      ]
        .join(" ")
        .toLowerCase();
      const matchesSearch = !term || haystack.includes(term);
      return matchesSearch;
    });
  }, [orgs, search]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageOrgs = useMemo(
    () => filtered.slice(page * pageSize, page * pageSize + pageSize),
    [filtered, page, pageSize],
  );
  const pageStart = filtered.length === 0 ? 0 : page * pageSize + 1;
  const pageEnd = filtered.length === 0 ? 0 : page * pageSize + pageOrgs.length;

  useEffect(() => {
    setPage(0);
  }, [search, pageSize]);

  useEffect(() => {
    if (page >= pageCount) {
      setPage(Math.max(0, pageCount - 1));
    }
  }, [page, pageCount]);

  const createMut = useMutation({
    mutationFn: async () =>
      createOrganization(
        {
          name: form.name.trim(),
          contact_email: form.contact_email || null,
          phone: form.phone || null,
          website: form.website || null,
          assignee_name: form.assignee_name || null,
          assignee_email: form.assignee_email || null,
          is_active: form.is_active,
          tag_ids: form.tag_ids,
          new_tags: form.new_tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean),
          first_location: {
            address_line_1: form.location_address_line_1.trim(),
            address_line_2: form.location_address_line_2.trim() || null,
            city: form.location_city.trim(),
            state_id: form.location_state_id,
            pincode: form.location_pincode.trim(),
            is_primary: form.location_is_primary,
            note: form.location_note.trim() || null,
          },
        },
      ),
    onSuccess: async () => {
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setToast(`Organization "${form.name.trim()}" created.`);
      await qc.invalidateQueries({ queryKey: ["organizations"] });
    },
  });

  const updateMut = useMutation({
    mutationFn: async () => {
      if (!editingOrg) {
        return null;
      }
      return updateOrganization(editingOrg.id, {
        name: form.name.trim(),
        contact_email: form.contact_email || null,
        phone: form.phone || null,
        website: form.website || null,
        assignee_name: form.assignee_name || null,
        assignee_email: form.assignee_email || null,
        is_active: form.is_active,
        tag_ids: form.tag_ids,
        new_tags: form.new_tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
      });
    },
    onSuccess: async () => {
      const nextName = form.name.trim();
      setEditingOrg(null);
      setForm(EMPTY_FORM);
      setToast(`Organization "${nextName}" updated.`);
      await qc.invalidateQueries({ queryKey: ["organizations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (organizationId: string) =>
      deleteOrganization(organizationId),
    onSuccess: async (_, organizationId) => {
      const deleted = orgs.find((item) => item.id === organizationId);
      setToast(`Organization "${deleted?.name ?? "record"}" deleted.`);
      await qc.invalidateQueries({ queryKey: ["organizations"] });
    },
  });

  function buildLocationPayload() {
    const parseNumber = (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    };
    return {
      name: locationForm.name.trim(),
      address_line_1: locationForm.address_line_1.trim(),
      address_line_2: locationForm.address_line_2.trim() || null,
      city: locationForm.city.trim(),
      state_id: locationForm.state_id,
      pincode: locationForm.pincode.trim(),
      latitude: parseNumber(locationForm.latitude),
      longitude: parseNumber(locationForm.longitude),
      is_primary: locationForm.is_primary,
      note: locationForm.note.trim() || null,
    };
  }

  const createLocationMut = useMutation({
    mutationFn: async () => {
      if (!selectedOrgId) {
        return null;
      }
      return createOrganizationLocation(selectedOrgId, buildLocationPayload());
    },
    onSuccess: async () => {
      setShowLocationModal(false);
      setEditingLocation(null);
      setLocationForm(EMPTY_LOCATION_FORM);
      setToast("Location created.");
      await qc.invalidateQueries({ queryKey: ["organization-locations"] });
    },
  });

  const updateLocationMut = useMutation({
    mutationFn: async () => {
      if (!selectedOrgId || !editingLocation) {
        return null;
      }
      return updateOrganizationLocation(selectedOrgId, editingLocation.id, buildLocationPayload());
    },
    onSuccess: async () => {
      setShowLocationModal(false);
      setEditingLocation(null);
      setLocationForm(EMPTY_LOCATION_FORM);
      setToast("Location updated.");
      await qc.invalidateQueries({ queryKey: ["organization-locations"] });
    },
  });

  const deleteLocationMut = useMutation({
    mutationFn: async (locationId: string) => {
      if (!selectedOrgId) {
        return null;
      }
      return deleteOrganizationLocation(selectedOrgId, locationId);
    },
    onSuccess: async () => {
      setToast("Location deleted.");
      await qc.invalidateQueries({ queryKey: ["organization-locations"] });
    },
    onError: (error) => {
      setToast(errorDetail(error));
    },
  });

  function openCreate() {
    if (!canCreateOrganizations) {
      return;
    }
    setEditingOrg(null);
    setForm(EMPTY_FORM);
    setShowCreate(true);
  }

  function openEdit(org: OrgItem) {
    setShowCreate(false);
    setForm({
      name: org.name,
      contact_email: org.contact_email ?? "",
      phone: org.phone ?? "",
      website: org.website ?? "",
      location_address_line_1: "",
      location_address_line_2: "",
      location_city: "",
      location_state_id: "",
      location_pincode: "",
      location_is_primary: true,
      location_note: "",
      tag_ids: org.tags.map((tag) => tag.id),
      new_tags: "",
      assignee_name: org.assignee_name ?? "",
      assignee_email: org.assignee_email ?? "",
      is_active: org.is_active,
    });
    setEditingOrg(org);
  }

  function openCreateLocation() {
    if (!selectedOrg) {
      return;
    }
    setEditingLocation(null);
    setLocationForm(EMPTY_LOCATION_FORM);
    setShowLocationModal(true);
  }

  function selectLocationsOrg(org: OrgItem) {
    setSelectedOrgId(org.id);
    setEditingLocation(null);
    setShowLocationModal(false);
  }

  function openEditLocation(location: OrgLocationItem) {
    setShowLocationModal(false);
    setEditingLocation(location);
    setLocationForm({
      name: location.name,
      address_line_1: location.address_line_1 ?? "",
      address_line_2: location.address_line_2 ?? "",
      city: location.city ?? "",
      state_id: location.state_id ?? "",
      pincode: location.pincode ?? "",
      latitude: location.latitude != null ? String(location.latitude) : "",
      longitude: location.longitude != null ? String(location.longitude) : "",
      is_primary: location.is_primary,
      note: location.note ?? "",
    });
    setShowLocationModal(true);
  }

  return (
    <EnterprisePageShell
      hideHeader
      kicker="Organization"
      title="Organizations"
      description="Global organization records with tags, contacts, and organization locations."
    >
      <div style={S.page}>
        {showCreate ? (
          <OrganizationModal
            title="Create organization"
            form={form}
            setForm={setForm}
            stateOptions={stateOptions}
            tagOptions={tagOptions}
            includeFirstLocation
            submitting={createMut.isPending}
            onClose={() => setShowCreate(false)}
            onSubmit={() => void createMut.mutateAsync()}
          />
        ) : null}

        {editingOrg ? (
          <OrganizationModal
            title="Edit organization"
            form={form}
            setForm={setForm}
            stateOptions={stateOptions}
            tagOptions={tagOptions}
            submitting={updateMut.isPending}
            onClose={() => setEditingOrg(null)}
            onSubmit={() => void updateMut.mutateAsync()}
          />
        ) : null}

        {showLocationModal && selectedOrg ? (
          <LocationModal
            title={editingLocation ? "Edit location" : "Create location"}
            form={locationForm}
            setForm={setLocationForm}
            stateOptions={stateOptions}
            submitting={editingLocation ? updateLocationMut.isPending : createLocationMut.isPending}
            onClose={() => {
              setShowLocationModal(false);
              setEditingLocation(null);
            }}
            onSubmit={() =>
              void (editingLocation ? updateLocationMut.mutateAsync() : createLocationMut.mutateAsync())
            }
          />
        ) : null}

        {q.isLoading ? (
          <p className="p-4  text-caption text-muted">
            Loading organizations...
          </p>
        ) : q.isError ? (
          <p className="p-4  text-caption text-error">
            {q.error instanceof Error
              ? q.error.message
              : "Failed to load organizations"}
          </p>
        ) : (
          <>
            {selectedOrg ? (
              <LocationsPanel
                org={selectedOrg}
                locations={locations}
                canCreate={canCreateLocationForOrg(selectedOrg)}
                canEdit={canEditLocationForOrg(selectedOrg)}
                canDelete={canDeleteLocationForOrg(selectedOrg)}
                loading={locationsQuery.isLoading}
                error={
                  locationsQuery.isError
                    ? locationsQuery.error instanceof Error
                      ? locationsQuery.error.message
                      : "Failed to load locations"
                    : null
                }
                onBack={() => {
                  setSelectedOrgId(null);
                  setEditingLocation(null);
                  setShowLocationModal(false);
                }}
                onAdd={openCreateLocation}
                onEdit={openEditLocation}
                onDelete={(location) => {
                  if (!confirm(`Delete location "${location.name}"?`)) {
                    return;
                  }
                  void deleteLocationMut.mutateAsync(location.id);
                }}
              />
            ) : (
            <ListSurface>
              <>
                <ListSurfaceHeader
                  title="Organization listing"
                  description="Search across name, contact details, assignee, and identifier to quickly locate any organization."
                  meta={
                    <ListCount
                      total={orgs.length}
                      filtered={filtered.length}
                      noun={filtered.length === 1 ? "organization" : "organizations"}
                    />
                  }
                  controls={
                    <ListToolbar>
                      <ListToolbarGroup grow className="lg:flex-[0_1_40%]">
                        <input
                          className={tableHeaderSearchInputClass}
                          style={{ minWidth: "280px", width: "100%", maxWidth: "36rem" }}
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search name, contact, website, or ID..."
                        />
                      </ListToolbarGroup>
                      <ListToolbarGroup className="ml-auto shrink-0">
                        {search.trim() ? (
                          <ToolbarIconButton icon={FilterX} label="Clear filters" onClick={() => setSearch("")} />
                        ) : null}
                        {canCreateOrganizations ? (
                          <ToolbarIconButton icon={Plus} label="Create organization" variant="primary" onClick={openCreate} />
                        ) : null}
                      </ListToolbarGroup>
                    </ListToolbar>
                  }
                />

                {!orgs.length ? (
                <div style={{ padding: "22px" }}>
                  <div style={S.emptyCard}>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#1f2937",
                      }}
                    >
                      No organizations yet
                    </div>
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "13px",
                        color: "var(--muted)",
                      }}
                    >
                      Create your first organization to start building the hierarchy.
                    </div>
                    {canCreateOrganizations ? (
                      <div style={{ marginTop: "16px" }}>
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={openCreate}
                        >
                          Create organization
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
                ) : (
                <>
                <ListSurfaceBody style={S.tableWrap}>
                  <table style={S.table}>
                    <thead style={S.thead}>
                      <tr>
                        <th style={S.th}>Name</th>
                        <th style={S.th}>Tags</th>
                        <th style={S.th}>Contact</th>
                        <th style={S.th}>Status</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageOrgs.length ? (
                        pageOrgs.map((o, index) => (
                          <tr
                            key={o.id}
                            style={{
                              background:
                                o.id === selectedOrgId
                                  ? "rgba(16,185,129,0.08)"
                                  : index % 2 === 1
                                    ? "rgba(59,130,246,0.05)"
                                    : "var(--bg-surface)",
                            }}
                          >
                            <td style={S.td}>
                              <div style={{ fontWeight: 600, color: "var(--foreground)" }}>
                                {o.name}
                              </div>
                            </td>
                            <td style={S.td}>
                              {o.tags.length ? (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                  {o.tags.map((tag) => (
                                    <span key={tag.id} style={{ ...S.badge, borderColor: "#dbeafe", background: "color-mix(in srgb, var(--accent) 10%, var(--card))", color: "#1d4ed8" }}>
                                      {tag.name}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color: "var(--muted)" }}>-</span>
                              )}
                            </td>
                            <td style={S.td}>
                              <div style={{ display: "grid", gap: "3px" }}>
                                <span>{o.contact_email ?? "-"}</span>
                                <span style={{ color: "var(--muted)", fontSize: "12px" }}>
                                  {[o.phone, o.website].filter(Boolean).join(" · ") || ""}
                                </span>
                              </div>
                            </td>
                            <td style={S.td}>
                              {o.is_active ? "Active" : "Inactive"}
                            </td>
                            <td style={S.td}>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <button
                                  type="button"
                                  className="inline-flex min-h-9 items-center justify-center gap-2 rounded-xl border border-primary/15 bg-primary/10 px-3 text-[12px] font-semibold text-primary transition-colors hover:border-primary/25 hover:bg-primary/15"
                                  onClick={() => selectLocationsOrg(o)}
                                >
                                  <MapPin size={14} />
                                  Locations
                                </button>
                                {canEditOrg(o) ? (
                                  <button
                                    type="button"
                                    className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-xl border border-primary/15 bg-primary/10 px-[10px] text-primary transition-colors hover:border-primary/25 hover:bg-primary/15"
                                    onClick={() => openEdit(o)}
                                    title={`Edit ${o.name}`}
                                  >
                                    <Pencil size={14} />
                                  </button>
                                ) : null}
                                {canDeleteOrganizations ? (
                                  <button
                                    type="button"
                                    className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-xl border border-[rgb(255_170_170)] bg-[rgb(255_188_188)] px-[10px] text-[#7f1d1d] transition-colors hover:border-[rgb(207_81_81)] hover:bg-[rgb(255_188_188)]"
                                    onClick={() => {
                                      if (!confirm(`Delete organization "${o.name}"?`)) {
                                        return;
                                      }
                                      void deleteMut.mutateAsync(o.id);
                                    }}
                                    title={`Delete ${o.name}`}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "34px 16px",
                              textAlign: "center",
                              color: "#9ca3af",
                              fontSize: "13px",
                            }}
                          >
                            No organizations match the current filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </ListSurfaceBody>
                <ListSurfaceFooter
                  left={
                    <label className="flex items-center gap-ds-2">
                      <span className="text-caption text-muted">Rows per page</span>
                      <ThemedSelect
                        value={String(pageSize)}
                        onChange={(value) => setPageSize(Number(value))}
                        ariaLabel="Rows per page"
                        className="w-[5.5rem]"
                        triggerClassName="h-10 border-border bg-card hover:border-primary/20"
                        options={ORG_PAGE_SIZE_OPTIONS.map((option) => ({ value: String(option), label: String(option) }))}
                      />
                    </label>
                  }
                  right={
                    <>
                      <span className="text-caption text-muted">
                        {filtered.length === 0 ? "No pages" : `Showing ${pageStart}-${pageEnd} of ${filtered.length}`}
                      </span>
                      <div className="flex items-center gap-ds-2">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={page === 0}
                          onClick={() => setPage((current) => Math.max(0, current - 1))}
                        >
                          Previous
                        </button>
                        <span className="text-caption tabular-nums text-muted">
                          Page {page + 1} / {pageCount}
                        </span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={page + 1 >= pageCount}
                          onClick={() => setPage((current) => current + 1)}
                        >
                          Next
                        </button>
                      </div>
                    </>
                  }
                />
</>
              )}
            </>
          </ListSurface>
        )}
      </>
    )}
      </div>
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
    </EnterprisePageShell>
  );
}
